import React, { useState } from "react";
import {
	Modal,
	Box,
	Typography,
	TextField,
	Button,
	CircularProgress,
	IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HuntGroup from "../interface/huntGroup";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import Agency from "../../../interfaces/agency";
import Client from "../../../interfaces/client";
import DropDownValue from "../../baseComponents/dropdownvalue";

interface AddTemplateModalProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (newTemplateRequest: NewTemplateRequest) => Promise<void>;
	huntGroups: HuntGroup[];
}

const modalStyle = {
	position: 'absolute' as 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 500,
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 4,
	borderRadius: 2,
};

export interface NewTemplateRequest {
	templateName: string,
	templateNumber: string,
	selectedHuntGroup: string,
	selectedAgency: string,
	clientName: string
}

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({ open, onClose, onSubmit, huntGroups }) => {
	// Form State
	const [templateName, setTemplateName] = useState("");
	const [templateNumber, setTemplateNumber] = useState("");
	const [selectedHuntGroup, setSelectedHuntGroup] = useState<string>("");
	const [selectedAgency, setSelectedAgency] = useState<string>("");
	const [clientName, setClientName] = useState("");

	// Data State
	const agencies: Agency[] = useSelector((state: RootState) => state.agencies.agencies);
	const clients: Client[] = useSelector((state: RootState) => state.clients.clients);

	const [loading, setLoading] = useState<boolean>(false);

	const handleClose = () => {
		// Reset state on close
		setTemplateName("");
		setTemplateNumber("");
		setSelectedHuntGroup("");
		setSelectedAgency("");
		setClientName("");
		onClose();
	};

	const handleSubmit = async () => {
		setLoading(true);
		if (templateName.length > 0 && templateNumber.length > 0 && selectedHuntGroup && selectedAgency) {
			await onSubmit({
				templateName,
				templateNumber,
				selectedHuntGroup: selectedHuntGroup,
				selectedAgency: selectedAgency,
				clientName
			});
		} else {
			alert("All fields are mandatory");
		}
		setLoading(false);
	};

	return (
		<Modal open={open} onClose={handleClose}>
			<Box sx={modalStyle}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography variant="h6">Add New Template</Typography>
					<IconButton onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
					<TextField
						label="Template Name"
						variant="outlined"
						fullWidth
						value={templateName}
						onChange={(e) => setTemplateName(e.target.value)}
					/>

					<TextField
						label="Scheduled Template Number"
						variant="outlined"
						fullWidth
						value={templateNumber}
						onChange={(e) => setTemplateNumber(e.target.value)}
					/>

					<DropDownValue 
						label="Select Hunt Group" 
						items={huntGroups} 
						selectedValue={selectedHuntGroup} 
						onSelection={(e: string) => setSelectedHuntGroup(e)} 
						keyProp="hunt_group_id"
						valueProp="hunt_group_id"
						displayProp="hunt_group_name"
					/>

					<DropDownValue 
						label="Select Agency" 
						items={agencies} selectedValue={selectedAgency} 
						onSelection={(e: string) => setSelectedAgency(e)} 
						keyProp="agency_name"
						valueProp="agency_name"
						displayProp="agency_name"
					/>

					<DropDownValue 
						label="Select Client" 
						items={clients}
						selectedValue={clientName} 
						onSelection={(e: string) => setClientName(e)} 
						keyProp="client_name"
						valueProp="client_name"
						displayProp="client_name"
					/>

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
						<Button onClick={handleClose} sx={{ mr: 2 }}>Cancel</Button>
						<Box mt={2} textAlign="right">
							<Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
								{loading ? <CircularProgress size={24} /> : "Add Template"}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
};

export default AddTemplateModal;